// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:id\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/hold/:id\",\"regex\":\"^/hold/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/airdrop-farming\"},{\"path\":\"/campaigns\"},{\"path\":\"/cede-arena\"},{\"path\":\"/click-and-earn\"},{\"path\":\"/coinbase\"},{\"path\":\"/cookies\"},{\"path\":\"/crypto-com-signup\"},{\"path\":\"/hall-of-fame\"},{\"path\":\"/hold\"},{\"path\":\"/kraken\"},{\"path\":\"/launch\"},{\"path\":\"/lbank\"},{\"path\":\"/lottery\"},{\"path\":\"/perp-algo-trade\"},{\"path\":\"/portfolio\"},{\"path\":\"/predictions\"},{\"path\":\"/privacy-policy\"},{\"path\":\"/profile\"},{\"path\":\"/quests\"},{\"path\":\"/region-blocked\"},{\"path\":\"/terms-and-conditions\"},{\"path\":\"/trade\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.3";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import { clientConfig } from "./lib/config/client-config";
import { processHubSentryEvent } from "./lib/sentry";
import * as Sentry from "@sentry/nextjs";

if (clientConfig.sentryEnv === "production") {
  Sentry.init({
    dsn: clientConfig.sentryDsn,
    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    release: clientConfig.sentryRelease,
    environment: clientConfig.sentryEnv,
    sendDefaultPii: true,

    // Propagates sentry-trace/baggage to the API so browser spans join the backend trace.
    // apps/api's tracesSampler inherits this decision, so it also sets the rate for hub-originated API transactions.
    tracesSampleRate: 0.03,
    tracePropagationTargets: [/^https:\/\/api\.cedehub\.io/],

    ignoreErrors: [
      /^Attempting to use a disconnected port object$/,
      /^Failed to connect to MetaMask$/,
      "window.Telegram[",
      'window["Telegram"]',
      "func sseError not found",
      // Browser/IndexedDB tab-lifecycle noise (closed tab, private mode, evicted store) — not an app bug.
      /Connection to indexed Database server lost/i,
    ],
    // Wallet user-rejections and provider disconnects arrive as object/string promise rejections
    // that `ignoreErrors` cannot match (the meaningful code/message is not in the event title),
    // so they are dropped here by EIP-1193 code instead.
    beforeSend: processHubSentryEvent,

    // Session Replay: record 100% of error sessions, 1% of healthy ones as a baseline.
    // The integration itself is added only on analytics consent (lib/sentry-replay), but it
    // reads these rates off the client options, so they must stay here.
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,

    integrations: [
      Sentry.browserTracingIntegration({
        // Hyperliquid's uncompressed ~330KB market list trips the "Large HTTP payload" detector on every campaign page.
        shouldCreateSpanForRequest: (url) => !url.startsWith("https://api.hyperliquid.xyz"),
        // Span quota: file loads and UI freezes were ~70% of hub spans; fetch/XHR spans (what the detectors read) stay.
        ignoreResourceSpans: [
          "resource.script",
          "resource.img",
          "resource.image",
          "resource.link",
          "resource.css",
          "resource.iframe",
          "resource.other",
        ],
        enableLongAnimationFrame: false,
      }),
      // Filter out third-party errors (browser extensions, injected scripts, third-party widgets)
      // This integration marks our code at build time and filters out anything unmarked
      Sentry.thirdPartyErrorFilterIntegration({
        // Must match the applicationKey in next.config.js
        filterKeys: ["cede-hub-app"],
        // Defines how to handle errors that contain third party stack frames.
        // Possible values are:
        // - 'drop-error-if-contains-third-party-frames'
        // - 'drop-error-if-exclusively-contains-third-party-frames'
        // - 'apply-tag-if-contains-third-party-frames'
        // - 'apply-tag-if-exclusively-contains-third-party-frames'
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
      // Click/input breadcrumbs default to `tag.class`, which collapses to noise like `span.truncate` because
      // Tailwind utilities dominate `class`. Read component-identifying attributes instead — `data-sentry-*`
      // come from reactComponentAnnotation in next.config.js, the rest are app-authored when present.
      Sentry.breadcrumbsIntegration({
        dom: {
          serializeAttribute: ["data-sentry-component", "data-sentry-source-file", "data-testid", "aria-label"],
          maxStringLength: 200,
        },
      }),
    ],
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
